import { useEffect, useRef, useState } from "react";
import PropertyFacts from "../components/PropertyFacts";
import Spinner from "../components/Spinner";
import RegenerateBar from "../components/RegenerateBar";
import { callAI, normalizeAddressKey, track } from "../lib/api";
import { propFactsLine, parsePriceRange } from "../lib/property";
import { renderMD } from "../lib/markdown";

// Stage 1 — Valuation.
// Auto-runs on first mount. Silently retries up to MAX_TRIES times before
// showing a soft fallback. Never shows a hard error — the prose is always
// displayed even if the structured range card can't be extracted.

const MAX_TRIES = 3;
const RETRY_DELAY_MS = 2000;

const SYSTEM = "You are a senior real estate appraiser. You produce concise, data-backed fair-price reports for home sellers. Always use the web_search tool to find recent comparable sales before pricing. Numbers over words. No disclaimers.";

const buildPrompt = (address, factsLine, note) => `Property: ${address}
${factsLine}

Web-search recent active and sold comparable sales within 0.5 miles in the last 6 months. Then produce a fair listing-price report.
${note ? `\nSeller's guidance for this regeneration — factor this in: ${note}\n` : ""}
Output EXACTLY this format:

**Fair listing-price range: $X,XXX,XXX – $X,XXX,XXX**

**Recommended list price:** $X,XXX,XXX

**Why this range:**
- (3–4 bullets each with: address · beds/baths · sqft · sale price · $/sqft · sale date)

**Confidence:** Low / Medium / High — one sentence why.

Total response under 220 words. No preamble.`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function ValuationStage({ state, journey, update }) {
  const [loading, setLoading]   = useState(false);
  const [attempt, setAttempt]   = useState(0);   // which retry we're on
  const [softFail, setSoftFail] = useState(false);
  const fired = useRef(false);

  const run = async (isManual = false, note = "") => {
    setLoading(true);
    setSoftFail(false);
    update({ status: "in_progress" });
    if (isManual) track("seller_valuation_retry", { address: journey.address, has_note: Boolean(note) });
    else track("seller_valuation_run", { address: journey.address });

    let lastText = null;

    for (let i = 0; i < MAX_TRIES; i++) {
      setAttempt(i + 1);
      try {
        const { text, cached } = await callAI({
          system: SYSTEM,
          prompt: buildPrompt(journey.address, propFactsLine(journey.property), note),
          // A guided regeneration must hit the model fresh — skip the cache.
          cacheKey: note ? undefined : `valuation_v2:${normalizeAddressKey(journey.address)}`,
          cacheType: note ? undefined : "valuation",
        });
        if (cached) track("seller_valuation_cache_hit");

        // Accept the response if we got meaningful text back
        if (text && text.trim().length > 40) {
          const range = parsePriceRange(text);
          update({
            status: "done",
            data: { text, range, generated_at: new Date().toISOString() },
          });
          track("seller_valuation_done", { has_range: Boolean(range), attempt: i + 1 });
          setLoading(false);
          setAttempt(0);
          return;
        }
        lastText = text; // save even if short, use as last-resort
      } catch (_) {
        // network / server error — keep retrying
      }

      if (i < MAX_TRIES - 1) await sleep(RETRY_DELAY_MS);
    }

    // All retries exhausted. If we got any text at all, show it without a range card.
    if (lastText && lastText.trim().length > 0) {
      update({
        status: "done",
        data: { text: lastText, range: null, generated_at: new Date().toISOString() },
      });
    } else {
      // Nothing came back — soft fail: keep status pending so the user can retry
      setSoftFail(true);
      update({ status: "pending" });
    }

    setLoading(false);
    setAttempt(0);
  };

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (state.status !== "done" && !state.data) run();
  }, []); // eslint-disable-line

  const data = state.data;

  const spinnerLabel = attempt > 1
    ? `Pulling comps… (attempt ${attempt} of ${MAX_TRIES})`
    : "Pulling comps and running valuation…";

  return (
    <>
      <PropertyFacts property={journey.property} />

      {loading && <Spinner label={spinnerLabel} />}

      {!loading && data && (
        <>
          {data.range && (
            <div className="s-val-card">
              <div className="s-val-range">
                ${Math.round(data.range.low).toLocaleString()} – ${Math.round(data.range.high).toLocaleString()}
              </div>
              <div className="s-val-mid">Estimated fair listing range</div>
            </div>
          )}
          <div className="s-prose" dangerouslySetInnerHTML={{ __html: renderMD(data.text) }} />
          <RegenerateBar
            onRegenerate={(note) => run(true, note)}
            loading={loading}
            placeholder="e.g. weight recent sales more, factor in the pool…"
          />
        </>
      )}

      {!loading && softFail && (
        <div style={{ textAlign: "center", padding: "28px 16px" }}>
          <p style={{ fontSize: 13, color: "#7aad98", marginBottom: 16, lineHeight: 1.6 }}>
            Valuation is taking longer than usual. Check your connection and try again.
          </p>
          <button type="button" className="s-btn amber" onClick={() => run(true)}>
            Try again
          </button>
        </div>
      )}
    </>
  );
}
